import { apiRequest } from './apiClient';

export const PARENT_NOTIFICATION_TYPE = 'ParentNotification';

export function isParentNotification(notification) {
  return notification?.typeNotification === PARENT_NOTIFICATION_TYPE;
}

export function filterStudentNotifications(notifications = []) {
  return notifications.filter((notification) => !isParentNotification(notification));
}

export function filterParentNotifications(notifications = []) {
  return notifications.filter(isParentNotification);
}

export function countUnreadNotifications(notifications = []) {
  return notifications.filter((notification) => !notification.isSeen).length;
}

export async function getUnreadNotificationCounts() {
  const notifications = await getReceivedNotifications();

  return {
    student: countUnreadNotifications(filterStudentNotifications(notifications)),
    parent: countUnreadNotifications(filterParentNotifications(notifications)),
  };
}

export async function getReceivedNotifications() {
  const response = await apiRequest('/student/notifications/received');

  return response.data || [];
}

export async function getNotificationById(notificationId) {
  const response = await apiRequest(`/student/notifications/${notificationId}`);

  return response.data || null;
}

export async function markNotificationAsSeen(notificationId) {
  const response = await apiRequest(`/student/notifications/${notificationId}/seen`, {
    method: 'PATCH',
  });

  return response.data || null;
}

export async function getNotificationReceivers() {
  const response = await apiRequest('/student/notifications/receivers');

  return response.data || { teachers: [], admins: [] };
}

export async function sendNotification({
  receiverId,
  receiverRole,
  senderRole,
  title,
  description,
}) {
  const response = await apiRequest('/student/notifications', {
    method: 'POST',
    body: {
      receiverId,
      receiverRole,
      ...(senderRole ? { senderRole } : {}),
      typeNotification: 'Notification',
      title,
      description,
    },
  });

  return response.data || null;
}

export async function getSentNotifications() {
  const response = await apiRequest('/student/notifications/sent');

  return response.data || [];
}

export async function deleteSentNotification(notificationId) {
  const response = await apiRequest(`/student/notifications/${notificationId}`, {
    method: 'DELETE',
  });

  return response.data || null;
}

export async function updateSentNotification(notificationId, data) {
  const response = await apiRequest(`/student/notifications/${notificationId}`, {
    method: 'PATCH',
    body: data,
  });

  return response.data || null;
}

export function canEditNotification(notification, currentUserId) {
  if (!notification || isSystemNotification(notification) || isEventNotification(notification)) {
    return false;
  }

  const senderId = notification?.sender?.id ?? notification?.senderId;
  return Boolean(currentUserId && senderId && senderId === currentUserId);
}

export function isBatchSentItem(item) {
  return (
    Boolean(item?.batchId) ||
    item?.target?.type === 'class' ||
    item?.target?.type === 'all_students' ||
    (item?.receiverCount ?? 0) > 1
  );
}

export function filterSentNotificationsBySender(notifications = [], senderRole) {
  return notifications.filter((notification) => notification.senderRole === senderRole);
}

export function getNotificationSenderTitle(notification, { currentUserId } = {}) {
  const senderId = notification?.sender?.id ?? notification?.senderId;

  if (currentUserId && senderId === currentUserId) {
    return 'شما';
  }

  const title = notification?.sender?.title?.trim();

  if (title) {
    return title;
  }

  return 'سیستم';
}

export function isSystemNotification(notification) {
  if (notification?.sender) {
    return notification.sender.id == null && notification.sender.role == null;
  }

  return !notification?.senderId;
}

export function isEventNotification(notification) {
  return Boolean(notification?.eventType || notification?.resourceType);
}

export function filterMessageNotifications(notifications = []) {
  return notifications.filter((notification) => !isEventNotification(notification));
}

export function filterEventNotifications(notifications = []) {
  return notifications.filter(isEventNotification);
}

const EVENT_TYPE_LABELS = {
  HomeworkGraded: 'تصحیح تکلیف',
  HomeworkSubmitted: 'ارسال تکلیف',
  HomeworkAssigned: 'تکلیف جدید',
  ExamGraded: 'تصحیح آزمون',
  ExamPublished: 'آزمون جدید',
  ChallengeGraded: 'بررسی چالش',
};

const RESOURCE_TYPE_LABELS = {
  HomeworkSubmission: 'تکلیف',
  Homework: 'تکلیف',
  ExamSubmission: 'آزمون',
  Exam: 'آزمون',
  ChallengeSubmission: 'چالش',
  Challenge: 'چالش',
};

const RESOURCE_LIST_PATHS = {
  HomeworkSubmission: '/homeworks',
  Homework: '/homeworks',
  ExamSubmission: '/exams',
  Exam: '/exams',
  ChallengeSubmission: '/challenges',
  Challenge: '/challenges',
};

export function getEventNotificationLabel(notification) {
  if (notification?.eventType && EVENT_TYPE_LABELS[notification.eventType]) {
    return EVENT_TYPE_LABELS[notification.eventType];
  }

  if (notification?.resourceType && RESOURCE_TYPE_LABELS[notification.resourceType]) {
    return RESOURCE_TYPE_LABELS[notification.resourceType];
  }

  return 'اطلاعیه سیستم';
}

export function getEventNotificationResourcePath(notification) {
  return RESOURCE_LIST_PATHS[notification?.resourceType] || null;
}

export function getEventNotificationResourceLabel(notification) {
  const path = getEventNotificationResourcePath(notification);

  if (path === '/homeworks') {
    return 'مشاهده تکالیف';
  }

  if (path === '/exams') {
    return 'مشاهده آزمون‌ها';
  }

  if (path === '/challenges') {
    return 'مشاهده چالش‌ها';
  }

  return '';
}

export function canReplyToNotification(notification, currentUserId) {
  if (!notification || isSystemNotification(notification) || isEventNotification(notification)) {
    return false;
  }

  const targetType = notification.target?.type;
  const isOwnGroupSend =
    (targetType === 'class' || targetType === 'all_students') &&
    currentUserId &&
    notification.senderId === currentUserId;

  return !isOwnGroupSend;
}

export function normalizeNotificationThread(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  if (Array.isArray(payload.thread)) {
    return payload.thread;
  }

  if (Array.isArray(payload.messages)) {
    return payload.messages;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.notifications)) {
    return payload.notifications;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  return [];
}

export function sortNotificationThread(items = []) {
  return [...items].sort((a, b) => {
    const first = new Date(a?.createdAt || 0).getTime();
    const second = new Date(b?.createdAt || 0).getTime();

    return first - second;
  });
}

export async function getNotificationThread(notificationId) {
  const response = await apiRequest(`/student/notifications/${notificationId}/thread`);

  return sortNotificationThread(normalizeNotificationThread(response.data));
}

export async function replyToNotification(notificationId, { title, description }) {
  const payload = {
    description: description.trim(),
  };
  const trimmedTitle = title?.trim();

  if (trimmedTitle) {
    payload.title = trimmedTitle;
  }

  const response = await apiRequest(`/student/notifications/${notificationId}/reply`, {
    method: 'POST',
    body: payload,
  });

  return response.data || null;
}
